import React, { type FC, useState, useEffect } from "react";
import {
  Button as MuiButton,
  ButtonGroup,
  CircularProgress,
  Tooltip,
  type SxProps,
  type Theme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  MenuItem,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Button, ButtonVariantOption } from "@/components/Button";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useZenrinApi } from "@/features/map/hooks/useZenrinSearch";
import { formatNumberWithComma } from "@/utils/formatNumber";
import {
  MAP_MENU_SX,
  MAP_TOOLBAR_BUTTON_SX,
} from "@/features/map/constants/controlSize";
import { useIsMobile } from "@/hooks/useIsMobile";

/**
 * ZenrinNameplateButtonGroup コンポーネントのProps
 *
 * このコンポーネントは、MUI(Material-UI)を使用したゼンリン住宅地図取得機能の
 * ボタングループを提供する。ZenrinNameplateControlの代替UIとして使用可能
 */
export interface ZenrinNameplateButtonGroupProps {
  /** 範囲選択モードが有効かどうか */
  selectionMode: boolean;
  /** API通信中かどうか（ローディング状態） */
  loading: boolean;
  /** ゼンリン画像のリクエストが可能かどうか（ズームレベルや権限による制限） */
  canRequestZenrinImage: boolean;
  /** 範囲選択モードを開始するコールバック */
  onStart?: () => void;
  /** 履歴復元モーダルを開くコールバック */
  onOpenHistory?: () => void;
  /** 選択した範囲で画像取得を確定するコールバック */
  onConfirm?: () => Promise<void> | void;
  /** 範囲選択をキャンセルするコールバック */
  onCancel?: () => void;
}

/**
 * 通常状態のボタンスタイル
 * 白背景にプライマリカラーのアウトラインで、他のUIと調和するデザイン
 * textTransform: "none" で大文字変換を無効化し、日本語テキストを正しく表示
 */
const buttonSx: SxProps<Theme> = (theme) => ({
  textTransform: "none",
  bgcolor: theme.palette.common.white,
  color: theme.palette.primary.main,
  borderColor: theme.palette.primary.main,
  height: "40px",
  whiteSpace: "nowrap", // テキストの折り返しを防止
  "&:hover": {
    bgcolor: alpha(theme.palette.primary.main, 0.08),
    borderColor: theme.palette.primary.main,
  },
});

/**
 * 選択モード時のアクティブボタンスタイル
 * 緑系の色を使用して「アクティブ」「選択中」であることを視覚的に示す
 * 成功/確定のイメージを持つsuccessカラーパレットを活用
 */
const activeButtonSx: SxProps<Theme> = (theme) => ({
  ...buttonSx(theme),
  bgcolor: alpha(theme.palette.success.main, 0.1),
  color: theme.palette.success.dark,
  borderColor: theme.palette.success.main,
  "&:hover": {
    bgcolor: alpha(theme.palette.success.main, 0.2),
    borderColor: theme.palette.success.main,
  },
});

/**
 * 確定ボタンのスタイル
 * 緑色の塗りつぶしボタンで、主要なアクション（確定）であることを強調
 * 無効状態でも色味を保持し、ローディング中でもボタンの役割が分かるようにする
 */
const confirmButtonSx: SxProps<Theme> = (theme) => ({
  textTransform: "none",
  bgcolor: theme.palette.success.main,
  color: theme.palette.common.white,
  borderColor: theme.palette.success.main,
  height: "40px",
  whiteSpace: "nowrap",
  "&:hover": {
    bgcolor: theme.palette.success.dark,
    borderColor: theme.palette.success.dark,
  },
  // 無効状態でも視認性を維持するため、完全なグレーアウトではなく半透明にする
  "&.Mui-disabled": {
    bgcolor: alpha(theme.palette.success.main, 0.5),
    color: theme.palette.common.white,
    borderColor: alpha(theme.palette.success.main, 0.5),
  },
});

/**
 * キャンセルボタンのスタイル
 * グレー系の控えめなデザインで、破棄的なアクションであることを示す
 * 確定ボタンと対比させることで、ユーザーが誤操作しにくいUIを実現
 */
const cancelButtonSx: SxProps<Theme> = (theme) => ({
  textTransform: "none",
  bgcolor: theme.palette.common.white,
  color: theme.palette.grey[700],
  borderColor: theme.palette.grey[400],
  height: "40px",
  whiteSpace: "nowrap",
  "&:hover": {
    bgcolor: theme.palette.grey[100],
    borderColor: theme.palette.grey[500],
  },
  "&.Mui-disabled": {
    bgcolor: theme.palette.grey[100],
    color: theme.palette.grey[400],
  },
});

/**
 * ゼンリン住宅地図（表札あり）取得用のMUIボタングループコンポーネント
 *
 * 2つの表示モードを持つ:
 * 1. 非選択モード: 単一の「住宅地図（表札あり）」ボタンを表示
 * 2. 選択モード: ButtonGroupで3つのボタンを横並びに表示
 *    - メインボタン（無効化状態で選択中を示す）
 *    - 確定ボタン
 *    - キャンセルボタン
 *
 * ZenrinNameplateControlとは異なり、MUIコンポーネントを使用することで
 * アプリケーション全体のデザインシステムと一貫性を保つ
 */
export const ZenrinNameplateButtonGroup: FC<
  ZenrinNameplateButtonGroupProps
> = ({
  selectionMode,
  loading,
  canRequestZenrinImage,
  onStart,
  onOpenHistory,
  onConfirm,
  onCancel,
}) => {
  const isMobile = useIsMobile();
  // 確認ダイアログの表示状態
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);
  // 残り無料取得回数（null: 未取得またはエラー、number: 取得成功）
  const [remainingCount, setRemainingCount] = useState<number | null>(null);
  // 1枚あたり単価（円・税込）。null: 未取得またはエラー時はフォールバック表示
  const [unitPrice, setUnitPrice] = useState<number | null>(null);
  // 残り回数・単価取得中かどうか
  const [loadingRemainingCount, setLoadingRemainingCount] = useState(false);

  const { zenrinGetRemainingImageCount, zenrinGetImageRequestPrice } =
    useZenrinApi();

  // ダイアログが開いたタイミングで残り回数と単価を取得する
  useEffect(() => {
    if (!confirmDialogOpen) {
      setRemainingCount(null);
      setUnitPrice(null);
      setLoadingRemainingCount(false);
      return;
    }
    let cancelled = false;
    setLoadingRemainingCount(true);
    setRemainingCount(null);
    setUnitPrice(null);
    void Promise.all([
      zenrinGetRemainingImageCount(),
      zenrinGetImageRequestPrice(),
    ])
      .then(([budgetRes, priceRes]) => {
        if (cancelled) return;
        setLoadingRemainingCount(false);
        setRemainingCount(budgetRes?.remainingFreeCount ?? null);
        setUnitPrice(priceRes?.unitPrice ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadingRemainingCount(false);
        setRemainingCount(null);
        setUnitPrice(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    confirmDialogOpen,
    zenrinGetRemainingImageCount,
    zenrinGetImageRequestPrice,
  ]);

  // 各ボタンの無効状態を計算
  // メインボタン: ロード中、選択モード中、またはリクエスト不可時に無効
  const primaryDisabled = loading || selectionMode || !canRequestZenrinImage;
  // 確定ボタン: ロード中またはリクエスト不可時に無効
  const confirmDisabled = loading || !canRequestZenrinImage;
  // キャンセルボタン: ロード中のみ無効（API通信中の途中キャンセルを防ぐ）
  const cancelDisabled = loading;

  // 確定ボタンクリック時に確認ダイアログを表示
  const handleConfirmClick = (): void => {
    setConfirmDialogOpen(true);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setMenuAnchorEl(null);
  };

  const handleRequestClick = (): void => {
    onStart?.();
    setMenuAnchorEl(null);
  };

  const handleRestoreClick = (): void => {
    onOpenHistory?.();
    setMenuAnchorEl(null);
  };

  // ダイアログで「取得する」をクリック時の処理
  const handleDialogConfirm = (): void => {
    setConfirmDialogOpen(false);
    void onConfirm?.();
  };

  // ダイアログで「キャンセル」をクリック時の処理
  const handleDialogCancel = (): void => {
    setConfirmDialogOpen(false);
  };

  // 非選択モード時: シンプルな単一ボタンを表示
  if (!selectionMode) {
    return (
      <>
        <Tooltip
          title="住宅地図（表札あり）の範囲を選択して取得します"
          placement="top"
        >
          {/*
          span要素でラップする理由:
          MUIのTooltipは無効化されたボタンにはホバーイベントが発火しないため、
          span要素を介してTooltipを機能させる
        */}
          <span>
            <MuiButton
              onClick={handleMenuOpen}
              startIcon={<HomeIcon />}
              endIcon={<ArrowDropDownIcon />}
              // 地図の上の帯に置くので、他の地図の操作と同じ枠なしの文字ボタンにする
              sx={MAP_TOOLBAR_BUTTON_SX}
              disabled={primaryDisabled}
            >
              住宅地図（表札あり）
            </MuiButton>
          </span>
        </Tooltip>
        <Menu
          sx={MAP_MENU_SX}
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <MenuItem onClick={handleRequestClick}>新規で取得</MenuItem>
          <MenuItem onClick={handleRestoreClick}>履歴を復元</MenuItem>
        </Menu>
      </>
    );
  }

  // 選択モード時: 3つのボタンをグループ化して表示
  return (
    <>
      <ButtonGroup variant="outlined" sx={{ height: "40px" }}>
        {/*
        メインボタン: 選択モード中は常に無効化
        選択中であることを視覚的に示す役割のみ
      */}
        <Tooltip title="範囲選択中" placement="top">
          {/* SP は3つ並べると画面幅を超えるので、アイコンだけにして確定・キャンセルを収める */}
          {isMobile ? (
            <MuiButton
              aria-label="住宅地図（表札あり）の範囲選択中"
              sx={activeButtonSx}
              disabled
            >
              <HomeIcon fontSize="small" />
            </MuiButton>
          ) : (
            <MuiButton startIcon={<HomeIcon />} sx={activeButtonSx} disabled>
              住宅地図（表札あり）
            </MuiButton>
          )}
        </Tooltip>

        {/* 確定ボタン: 選択した範囲でゼンリン画像を取得 */}
        <Tooltip title="選択した範囲で住宅地図を取得します" placement="top">
          <span>
            <MuiButton
              onClick={handleConfirmClick}
              startIcon={
                // ローディング中はスピナーを表示し、処理中であることを示す
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckIcon />
                )
              }
              sx={confirmButtonSx}
              variant="contained"
              disabled={confirmDisabled}
            >
              {loading ? "取得中..." : "確定"}
            </MuiButton>
          </span>
        </Tooltip>

        {/* キャンセルボタン: 範囲選択モードを解除 */}
        <Tooltip title="範囲選択をキャンセルします" placement="top">
          <span>
            <MuiButton
              onClick={() => {
                onCancel?.();
              }}
              startIcon={<CloseIcon />}
              sx={cancelButtonSx}
              variant="outlined"
              disabled={cancelDisabled}
            >
              キャンセル
            </MuiButton>
          </span>
        </Tooltip>
      </ButtonGroup>

      {/* 確認ダイアログ */}
      <Dialog
        open={confirmDialogOpen}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          setConfirmDialogOpen(false);
        }}
        aria-labelledby="zenrin-nameplate-confirm-title"
        aria-describedby="zenrin-nameplate-confirm-description"
      >
        <DialogTitle id="zenrin-nameplate-confirm-title">確認</DialogTitle>
        <DialogContent>
          <DialogContentText id="zenrin-nameplate-confirm-description">
            {((): React.ReactNode | null => {
              const price = unitPrice ?? 550;
              if (loadingRemainingCount) {
                return (
                  <>
                    あと---回、無料で取得できます。 それ以降は
                    {formatNumberWithComma(price)}
                    円/回（税込）となります。
                    <br />
                    指定の範囲で、表札あり住宅地図を取得しますか？
                  </>
                );
              }
              if (remainingCount === null) {
                return (
                  <>
                    所定の回数を超えた場合は{formatNumberWithComma(price)}
                    円/回（税込）となります。
                    <br />
                    指定の範囲で、表札あり住宅地図を取得しますか？
                  </>
                );
              }
              if (remainingCount > 0) {
                return (
                  <>
                    あと{formatNumberWithComma(remainingCount)}
                    回、無料で取得できます。 それ以降は
                    {formatNumberWithComma(price)}円/回（税込）となります。
                    <br />
                    指定の範囲で、表札あり住宅地図を取得しますか？
                  </>
                );
              }
              if (remainingCount === 0) {
                return `今月の無料回数は残り0回のため、${formatNumberWithComma(
                  price
                )}円/回（税込）となります。指定の範囲で、表札あり住宅地図を取得しますか？`;
              }
              return null;
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", mb: 1 }}>
          <Button
            label="キャンセル"
            variant={ButtonVariantOption.Outlined}
            onClick={handleDialogCancel}
          />
          <Button
            label="取得する"
            variant={ButtonVariantOption.Contained}
            onClick={handleDialogConfirm}
            disabled={loading}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
